import React, { useState, useEffect } from 'react';
import { LoanProperty } from '../LoanComponents';
import Tabs from 'devextreme-react/tabs';
import { v4 as uuidv4 } from 'uuid';
import { LoanType } from '../LoanConstants';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import { array_move } from '../../../common/SharedFunctions';
import { StructureTypeOptions } from '../LoanConstants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';

export const LoanProperties = ({
    bfiNumber,
    commitment,
    originationFee,
    properties,
    onChange,
    onRemove,
    loanTypeId,
    statusId,
    validateSubmission,
    required = true,
}) => {
    const styles = {
        formRow: { marginBottom: '0' },
        formColumn: { paddingBottom: '3px' },
        propertyTitle: {
            backgroundColor: 'aliceblue',
            margin: '0',
            padding: '5px',
            marginBottom: '10px',
            position: 'relative',
        },
        property_box: {
            border: '1px solid silver',
            borderRadius: '0 0 10px 10px',
            padding: '10px',
        },
    };

    const [tabs, set_tabs] = useState([]);
    const [tabIndex, set_tabIndex] = useState(0);
    const [propertyIndex, set_propertyIndex] = useState(0);

    useEffect(() => {
        if (!properties || properties.length === 0) {
            return;
        }

        if (properties.length > 1 && tabs.length < properties.length + 1) {
            genTabs(properties);
            set_tabIndex(0);
            set_propertyIndex(-1);
        }

        const inValidIndex = properties.findIndex((p) => p.inValid);
        if (inValidIndex > -1) {
            properties[inValidIndex].inValid = false;
            set_propertyIndex(inValidIndex);
            set_tabIndex(inValidIndex + 1);
        }
    }, [properties]);

    useEffect(() => {
        if (!properties && !originationFee) {
            return;
        }
        //Business rule implementation - if portfolio and originationFee exists at the loan level but no items at the property level then set a default
        if (properties.length > 1) {
            const propertiesWithoutOriginationFee = properties.filter((x) => !x.originationFee || x.originationFee == 0);
            if (originationFee && originationFee > 0 && propertiesWithoutOriginationFee.length == properties.length) {
                const defaultPropertyOriginationFee = originationFee / properties.length;
                if (defaultPropertyOriginationFee && defaultPropertyOriginationFee > 0)
                    properties.forEach((p) => {
                        p.originationFee = defaultPropertyOriginationFee;
                    });
            }
        }
    }, [originationFee]);

    const genTabs = (properties) => {
        const newtabs = [];
        properties.forEach((p, index) => {
            if (!p.guid) {
                p.guid = uuidv4();
            }
            newtabs.push({
                text: `.${(index + 1).toString().padStart(2, '0')}`,
            });
        });

        if (properties.length > 1) {
            newtabs.unshift({ text: 'Property Rollup' });
        }
        set_tabs(newtabs);
    };

    const getNewProperty = () => {
        return {
            guid: uuidv4(),
            id: 0,
            legalDescription: '',
            unitCount: null,
            bfiNumberSuffix: properties.length + 1,
            structureTypeId: '',
            landCost: '',
            verticalCost: '',
            threeMilePopulation: '',
            threeMileFiveYearGrowth: '',
            status: '',
            commitment: '',
            floodReport: {},
            inSpecialFloodHazardArea: false,
            street1: '',
            street2: '',
            city: '',
            state: '',
            zipCode: '',
            county: '',
            currentDebt: '',
            appraisals: [],
            hadPermitAtClosingTypeId: '',
            preferredInterestPaymentTypeId: '',
        };
    };

    const onPropertyAdd = (e) => {
        const newProperty = getNewProperty();
        properties.push(newProperty);

        const newTabs = [...tabs];
        newTabs.push({
            text: `.${properties.length.toString().padStart(2, '0')}`,
        });

        if (newTabs.length <= properties.length) {
            newTabs.unshift({ text: 'Property Rollup' });
        }
        set_tabs(newTabs);
        set_tabIndex(properties.length);
        set_propertyIndex(properties.length - 1);
        onChange && onChange(properties);
    };

    const onPropertyRemove = (index) => {
        properties.splice(index, 1);
        genTabs(properties);
        set_tabIndex(0);
        if (properties.length > 1) {
            set_propertyIndex(-1);
        } else {
            set_propertyIndex(properties.length - 1);
        }

        onRemove && onRemove(properties);
    };

    const onMoveLeft = (index) => {
        const newProps = array_move(properties, index, index - 1);
        newProps.forEach((p, index) => (p.bfiNumberSuffix = index + 1));
        set_tabIndex(tabIndex - 1);
        set_propertyIndex(propertyIndex - 1);
        onChange && onChange(newProps);
    };

    const onMoveRight = (index) => {
        const newProps = array_move(properties, index, index + 1);
        newProps.forEach((p, index) => (p.bfiNumberSuffix = index + 1));
        set_tabIndex(tabIndex + 1);
        set_propertyIndex(propertyIndex + 1);
        onChange && onChange(newProps);
    };

    const onPropertyChange = (prop) => {
        const index = properties.findIndex((p) => p.id === prop.id);
        if (index < 0) {
            return;
        }

        properties[index] = prop;
        onChange && onChange(properties);
    };

    const onTabsSelectionChanged = (args) => {
        if (args.name === 'selectedIndex') {
            if (properties.length > 1) {
                if (args.value === 0) {
                    set_propertyIndex(-1);
                } else {
                    set_propertyIndex(args.value - 1);
                }
            } else {
                set_propertyIndex(args.value);
            }
            set_tabIndex(args.value);
        }
    };

    const onStructureTypeRender = (e) => {
        const structureType = StructureTypeOptions.find((a) => a.id === e.data.structureTypeId);
        return structureType ? structureType.name : '';
    };

    const onNumberSuffixRender = (e) => {
        return e.data.bfiNumberSuffix.toString().padStart(2, '0');
    };

    const sortAppraisals = (apprs) => {
        if (!apprs) {
            return [];
        }
        return apprs.sort(function (a, b) {
            // Turn your strings into dates, and then subtract them
            // to get a value that is either negative, positive, or zero.
            return new Date(b.appraisedDate) - new Date(a.appraisedDate);
        });
    };

    const getLastAppraisal = (property) => {
        if (!property || !property.appraisals) {
            return null;
        }
        const sorted = sortAppraisals(property.appraisals);
        if (sorted.length === 0) {
            return null;
        }
        return sorted[0];
    };

    const onLtvRender = (e) => {
        const value = getLastAppraisal(e.data)?.appraisedValue;
        return value && e.data?.commitment ? ((e.data.commitment / value) * 100).toFixed(1) + '%' : '';
    };

    const onAppraisedValueRender = (e) => {
        return getLastAppraisal(e.data)?.appraisedValue;
    };

    const onAppraisedDateRender = (e) => {
        return getLastAppraisal(e.data)?.appraisedDate;
    };

    if (!properties) {
        return '';
    }

    return (
        <div style={{ marginTop: '10px', marginBottom: '10px' }}>
            <div>
                {loanTypeId !== LoanType.Model &&
                    loanTypeId !== LoanType.MasterNote &&
                    loanTypeId !== LoanType.SubNoteConstruction &&
                    loanTypeId !== LoanType.SubNoteFixFlip && (
                        <div
                            style={{
                                float: 'right',
                                marginRight: '12px',
                                marginTop: '-7px',
                            }}
                        >
                            <button
                                type='button'
                                className='button hollow'
                                onClick={onPropertyAdd}
                                title='Add new Property'
                                style={{ width: '40px' }}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                            </button>
                        </div>
                    )}
                <h2
                    className='decorated'
                    style={styles.sectionHeader}
                >
                    Property {properties.length > 1 ? ' Portfolio' : ''}
                </h2>
            </div>

            {tabs && tabs.length > 1 && (
                <Tabs
                    showNavButtons={true}
                    width={'50%'}
                    dataSource={tabs}
                    selectedIndex={tabIndex}
                    onOptionChanged={onTabsSelectionChanged}
                />
            )}

            <div style={properties.length > 1 ? styles.property_box : null}>
                {propertyIndex >= 0 && (
                    <>
                        {properties.length > 1 && (
                            <div style={{ textAlign: 'right' }}>
                                <button
                                    type='button'
                                    className='button hollow'
                                    onClick={(e) => onMoveLeft(propertyIndex)}
                                    title='Move Left'
                                    style={{
                                        marginBottom: '5px',
                                        width: '40px',
                                    }}
                                    disabled={propertyIndex < 1}
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </button>
                                &nbsp;
                                <button
                                    type='button'
                                    className='button hollow'
                                    onClick={(e) => onMoveRight(propertyIndex)}
                                    title='Move Right'
                                    style={{
                                        marginBottom: '5px',
                                        width: '40px',
                                    }}
                                    disabled={propertyIndex >= properties.length - 1}
                                >
                                    <FontAwesomeIcon icon={faArrowRight} />
                                </button>
                                &nbsp;
                                <button
                                    type='button'
                                    className='button hollow'
                                    onClick={(e) => onPropertyRemove(propertyIndex)}
                                    title='Remove Property'
                                    style={{
                                        marginBottom: '5px',
                                        width: '40px',
                                        color: '#c51455',
                                    }}
                                    disabled={!properties[propertyIndex].canDelete}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>
                        )}
                        <div>
                            <LoanProperty
                                property={properties[propertyIndex]}
                                onChange={(prop) => onPropertyChange(prop)}
                                bfiNumber={bfiNumber}
                                loanTypeId={loanTypeId}
                                statusId={statusId}
                                required={required}
                                commitment={commitment}
                                isPortfolio={properties.length > 1}
                                visible={true}
                                validateSubmission={validateSubmission}
                            />
                        </div>
                    </>
                )}
                {propertyIndex < 0 && (
                    <div style={{ minHeight: '567px' }}>
                        <DataGrid
                            dataSource={properties}
                            showBorders={true}
                            showRowLines={false}
                            rowAlternationEnabled={true}
                            remoteOperations={true}
                            allowColumnResizing={true}
                            allowColumnReordering={true}
                            columnResizingMode={'nextColumn'}
                            columnMinWidth={20}
                            columnAutoWidth={false}
                        >
                            <Column
                                dataField='bfiNumberSuffix'
                                caption='#'
                                alignment='left'
                                width={30}
                                cellRender={onNumberSuffixRender}
                            />
                            <Column
                                dataField='legalDescription'
                                caption='Description'
                                alignment='left'
                            />
                            <Column
                                dataField='city'
                                caption='City'
                                alignment='left'
                                width={100}
                            />
                            <Column
                                dataField='state'
                                caption='State'
                                alignment='left'
                                width={80}
                            />
                            <Column
                                dataField='zipCode'
                                caption='Zip'
                                alignment='left'
                                width={80}
                            />
                            <Column
                                caption='Type'
                                alignment='left'
                                cellRender={onStructureTypeRender}
                            />
                            <Column
                                dataField='unitCount'
                                caption='Units'
                                alignment='left'
                                width={50}
                            />
                            <Column
                                caption='Appr. Value'
                                alignment='left'
                                width={100}
                                dataType='number'
                                format='currency'
                                cellRender={onAppraisedValueRender}
                            />
                            <Column
                                dataField='appraisedDate'
                                caption='Appr. Date'
                                alignment='left'
                                width={80}
                                dataType='date'
                                cellRender={onAppraisedDateRender}
                            />
                            <Column
                                dataField='commitment'
                                caption='Amount'
                                alignment='left'
                                width={100}
                                dataType='number'
                                format='currency'
                            />
                            <Column
                                caption='LTV'
                                alignment='left'
                                width={80}
                                cellRender={onLtvRender}
                            />
                        </DataGrid>
                    </div>
                )}
            </div>
        </div>
    );
};
